import { useRef, useState } from 'react';

import {
  AdornedInput,
  Alert,
  Anchor,
  Box,
  Combobox,
  FormField,
  FormHelp,
  Input,
  Label,
  Select,
  Text,
} from '@zillow/constellation-10';
import { CharacterCountFormHelp } from '@zg-rentals/visual-cohesion';

import { useAddressAutocomplete } from '../../../../../hooks/queries/address';
import { isUnitNumberOverLimit } from '../../../../../utils/propertyValidationUtils';
import { useRefocusOnAddressInput, useRefocusOnUnitInput } from '../../hooks';
import { shouldDisplayRoomForRentCheckbox } from '../../utils';

import { UNIT_ALERT, type UnitAlertValue } from '../../../../../constants/AddPropertyConstants';
import AddressConstants from '../../../../../constants/AddressConstants';
import PropertyConstants from '../../../../../constants/PropertyConstants';
import { BEHAVIOR_OPTIONS } from '../../constants';

import RoomForRentCheckbox from '../RoomForRentCheckbox';

import type { ChangeEvent } from 'react';
import type { ComboboxOptionType } from '@zillow/constellation-10';
import type { BehaviorOption, ListingState } from '../../types';

const {
  PROPERTY_TYPES: { COMMUNITY, CONDO, HOUSE, MULTIPLEX, TOWNHOUSE },
} = PropertyConstants;

const MULTI_FAMILY_RESTRICTED_ERROR_MESSAGE =
  'Each unit at this address must be posted as a separate listing. Select the property type: Condo / apartment unit.';

const zillowAdvertisingURL = 'https://www.zillow.com/z/rental-property-advertising/';

const DEFAULT_VARIANT = 'default' as const;
const EMBEDDED_VARIANT = 'embedded' as const;

type Contract = {
  customerId?: string;
  name?: string;
};

type Props = {
  listing: ListingState;
  contracts?: {
    contracts: Array<Contract>;
  };
  errorObj: Partial<
    Record<keyof ListingState | 'listing', string | JSX.Element | Array<string | JSX.Element> | undefined>
  >;
  onListingChange: (keyName: keyof ListingState, newValue: string | boolean) => void;
  onCheckLocationRestriction: (fieldName?: keyof ListingState, fieldValue?: boolean | string) => Promise<true | void>;
  onCustomerSupportClick?: () => void;
  unitAlert?: UnitAlertValue | null;
  propertyAlreadyPostedWarningMessage?: string | Array<JSX.Element | string>;
  isSubmitDisabled?: boolean;
  setIsSubmitDisabled: (arg: boolean) => void;
  behavior?: BehaviorOption;
  isRefocusingAddressInput: boolean;
  setIsRefocusingAddressInput: (arg: boolean) => void;
  variant?: typeof DEFAULT_VARIANT | typeof EMBEDDED_VARIANT;
};

const AddPropertyForm = ({
  behavior = BEHAVIOR_OPTIONS.ADD,
  listing,
  contracts,
  errorObj,
  variant = DEFAULT_VARIANT,
  unitAlert = null,
  propertyAlreadyPostedWarningMessage = '',
  isSubmitDisabled = false,
  setIsSubmitDisabled,
  onListingChange,
  onCheckLocationRestriction,
  isRefocusingAddressInput,
  setIsRefocusingAddressInput,
}: Props) => {
  const { address, isRoomForRent, numUnits, propertyType, unit, customerId } = listing;

  const [setPartialAddress, queryData] = useAddressAutocomplete();
  const [isUnitFocused, setIsUnitFocused] = useState(false);
  const unitNumberInputElementRef = useRef<HTMLInputElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);

  const isUnitOverLimit = isUnitNumberOverLimit(unit);

  const isCommunityType = propertyType === COMMUNITY;
  const isMultiplexType = propertyType === MULTIPLEX;
  const isRoomForRentDisabled = isSubmitDisabled;

  const addressFieldError = errorObj.address || ((contracts?.contracts?.length ?? 0) <= 1 ? errorObj.customerId : null);

  useRefocusOnUnitInput(unitAlert !== null, isSubmitDisabled, unitNumberInputElementRef);
  useRefocusOnAddressInput(isRefocusingAddressInput, setIsRefocusingAddressInput, addressInputRef);

  const handleSelectAddress = async (option: ComboboxOptionType) => {
    const addressValue = typeof option === 'string' ? option : option.value;
    setIsSubmitDisabled(true);
    onListingChange('address', addressValue);
    await onCheckLocationRestriction('address', addressValue);
    setIsSubmitDisabled(false);
  };

  const handleAddressChange = (e: ChangeEvent<HTMLInputElement>) => {
    onListingChange('address', e.target.value);

    setPartialAddress(e.target.value);
  };

  const handleUpdateMFNumUnits = (e: ChangeEvent<HTMLInputElement>) => {
    onListingChange('numUnits', e.target.value);
  };

  const handleUpdateUnit = (e: ChangeEvent<HTMLInputElement>) => {
    onListingChange('unit', e.target.value);
  };

  const handleUpdateUnitBlur = () => {
    setIsUnitFocused(false);
    onCheckLocationRestriction();
  };

  const handleToggleRoomForRent = () => {
    const flippedIsRoomForRentValue = !isRoomForRent;
    onListingChange('isRoomForRent', flippedIsRoomForRentValue);
    onCheckLocationRestriction('isRoomForRent', flippedIsRoomForRentValue);
  };

  const handleUpdatePropertyType = (value: string) => {
    onListingChange('propertyType', value);
    onCheckLocationRestriction('propertyType', value);
  };

  const handleUpdateCustomerId = (e: ChangeEvent<HTMLInputElement>) => {
    onListingChange('customerId', e.target.value);
  };

  const renderUnitFormField = () => {
    switch (propertyType) {
      case COMMUNITY:
        return (
          <FormField
            css={{ marginTop: '300', marginBottom: '200' }}
            error={!!errorObj.numUnits}
            label={<Label>Number of units</Label>}
            control={
              <Input
                data-testid="number-of-units"
                onChange={handleUpdateMFNumUnits}
                onBlur={() => onCheckLocationRestriction()}
                value={numUnits || ''}
                disabled={isSubmitDisabled}
              />
            }
            description={<FormHelp>{errorObj.numUnits}</FormHelp>}
          />
        );
      case MULTIPLEX:
        return (
          <FormField
            css={{ marginTop: '300', marginBottom: '200' }}
            error={!!errorObj.numUnits}
            label={<Label>Number of units you own or manage</Label>}
            control={
              <Input
                data-testid="number-of-units-multiplex"
                onChange={handleUpdateMFNumUnits}
                value={numUnits || ''}
                disabled={isSubmitDisabled}
              />
            }
            description={<FormHelp>{errorObj.numUnits}</FormHelp>}
          />
        );
      default:
        return (
          <FormField
            css={{ marginBlock: '300' }}
            error={!!errorObj.unit || isUnitOverLimit}
            label={
              <Label>
                Unit number
                <Text textStyle="body-sm" className="fs-unmask">
                  &nbsp;(if applicable)
                </Text>
              </Label>
            }
            control={
              <AdornedInput
                startAdornment={<AdornedInput.Adornment>#</AdornedInput.Adornment>}
                input={
                  <AdornedInput.Input
                    ref={unitNumberInputElementRef}
                    data-testid="unit-number"
                    placeholder=""
                    onFocus={() => setIsUnitFocused(true)}
                    onBlur={handleUpdateUnitBlur}
                    onChange={handleUpdateUnit}
                    value={unit || ''}
                    disabled={isSubmitDisabled}
                  />
                }
              />
            }
            description={
              errorObj.unit && !isUnitOverLimit ? (
                <FormHelp>{errorObj.unit}</FormHelp>
              ) : (
                <CharacterCountFormHelp
                  currentLength={(unit || '').length}
                  maxLength={AddressConstants.UNIT_NUMBER_LENGTH_LIMIT}
                  isFocused={isUnitFocused}
                  label="Unit number"
                  helperText="Enter apartment, suite, or unit number"
                />
              )
            }
          />
        );
    }
  };

  return (
    <div data-testid="AddPropertyForm">
      {Boolean(unitAlert !== null || Boolean(propertyAlreadyPostedWarningMessage)) && (
        <Box css={{ display: 'flex', flexDirection: 'column', gap: '400', marginTop: '600' }}>
          {unitAlert === UNIT_ALERT.VALIDATED_WITHOUT_UNIT && (
            <Alert>
              We are unable to validate the property address with this unit number. Double-check before creating the
              property.
            </Alert>
          )}

          {unitAlert === UNIT_ALERT.PROMPT_FOR_UNIT && (
            <Alert>
              We&apos;ve detected this address may have a unit number. If your property has one, add it below.
              Otherwise, you can leave it blank.
            </Alert>
          )}

          {Boolean(propertyAlreadyPostedWarningMessage) && <Alert>{propertyAlreadyPostedWarningMessage}</Alert>}
        </Box>
      )}

      <FormField
        css={variant === EMBEDDED_VARIANT ? { marginBlock: '100' } : { marginBlock: '300', marginRight: { md: '400' } }}
        error={!!errorObj.address || !!addressFieldError}
        label={<Label id="street-address-label">Street address</Label>}
        control={
          <Combobox
            css={{ boxShadowColor: 'border.subtle', _hover: { boxShadowColor: 'border.action.neutral.hover' } }}
            appearance="input"
            focusFirstOption={true}
            options={
              queryData?.data?.addresses?.map((address) => ({
                ...address,
                value: address.name,
                label: address.name,
              })) ?? []
            }
            onClear={() => handleAddressChange({ target: { value: '' } } as ChangeEvent<HTMLInputElement>)}
            onOptionSelect={handleSelectAddress}
            selectCallbackFrequency="always"
            renderInput={({ ...rest }) => {
              return (
                <Combobox.Input
                  {...rest}
                  data-testid="autocomplete"
                  ref={addressInputRef}
                  value={address}
                  disabled={isSubmitDisabled}
                  onChange={handleAddressChange}
                  aria-labelledby="street-address-label"
                />
              );
            }}
            renderEmptyState={() => (address as string).length <= 7 && null}
            freeForm={true}
            autoCompleteBehavior="none"
          />
        }
        description={
          addressFieldError ? (
            <FormHelp>
              <div>{addressFieldError}</div>
            </FormHelp>
          ) : (
            <FormHelp>
              Enter the USPS-validated address. You won’t be able to edit the address once you create the listing.
            </FormHelp>
          )
        }
      />

      <Box
        css={{
          paddingRight: { md: variant === EMBEDDED_VARIANT ? '0' : '600' },
          display: 'grid',
          gridTemplateColumns: variant === EMBEDDED_VARIANT ? '1fr' : 'repeat(2, 1fr)',
          gap: '400',
        }}
      >
        <Box css={{ gridColumnEnd: { base: 'span 2', md: 'span 1' } }}>
          <FormField
            css={{ marginBlock: variant === EMBEDDED_VARIANT ? '100' : '300' }}
            error={!!errorObj.propertyType}
            label={<Label>Property type</Label>}
            control={
              <div data-testid="propertyType-select">
                <Combobox
                  css={{ boxShadowColor: 'border.subtle', _hover: { boxShadowColor: 'border.action.neutral.hover' } }}
                  appearance="select"
                  autoCompleteBehavior="automatic"
                  disabled={isSubmitDisabled}
                  fluidDropdown
                  limitChipsShown={1}
                  placeholder="Select property type"
                  selectCallbackFrequency="change"
                  shouldAwaitInteractionResponse={true}
                  showLabelForValue={true}
                  size="md"
                  value={propertyType}
                  options={[
                    {
                      label: HOUSE,
                      meta: 'Single detached house',
                      value: HOUSE,
                    },
                    {
                      label: TOWNHOUSE,
                      meta: 'Multi-level home',
                      value: TOWNHOUSE,
                    },
                    {
                      label: 'Condo',
                      meta: 'Single unit in a building',
                      value: CONDO,
                    },
                    ...(behavior !== BEHAVIOR_OPTIONS.EDIT
                      ? [
                          {
                            label: MULTIPLEX,
                            meta: '(duplex to 24 units)',
                            value: MULTIPLEX,
                          },
                        ]
                      : []),
                    {
                      label: COMMUNITY,
                      meta: '(25+ units)',
                      value: COMMUNITY,
                    },
                  ]}
                  onChange={handleUpdatePropertyType}
                />
              </div>
            }
            description={
              <FormHelp data-testid="propertyType-error">
                <div>
                  {errorObj.propertyType}
                  {errorObj.propertyType === MULTI_FAMILY_RESTRICTED_ERROR_MESSAGE && (
                    <>
                      <br />
                      <span>
                        You can also contact&nbsp;
                        <Anchor href={zillowAdvertisingURL} target="_blank" rel="noopener noreferrer">
                          Zillow advertising
                        </Anchor>
                        &nbsp;to post this listing as an apartment community
                      </span>
                    </>
                  )}
                </div>
              </FormHelp>
            }
          />
        </Box>

        <Box css={{ gridColumnEnd: { base: 'span 2', md: 'span 1' } }}>{renderUnitFormField()}</Box>
      </Box>

      {(!isRoomForRent || isCommunityType) && (contracts?.contracts?.length ?? 0) > 1 && (
        <FormField
          css={{ marginBlock: variant === EMBEDDED_VARIANT ? '100' : '300' }}
          error={!!errorObj.customerId}
          data-testid="active-contracts"
          label={<Label>Active Contracts</Label>}
          control={
            <Select
              css={{ color: 'text.subtle', whiteSpace: 'pre' }}
              aria-label="Active contracts"
              value={customerId ?? ''}
              onChange={(e) => handleUpdateCustomerId(e as unknown as ChangeEvent<HTMLInputElement>)}
              disabled={isSubmitDisabled}
            >
              <option value="" disabled={true}>
                Select
              </option>
              {contracts?.contracts?.map((contract) => (
                <option key={contract.customerId} value={contract.customerId}>
                  {contract.name}
                </option>
              ))}
            </Select>
          }
        />
      )}

      {shouldDisplayRoomForRentCheckbox(isMultiplexType, isCommunityType, behavior) && (
        <RoomForRentCheckbox
          isRoomForRentChecked={isRoomForRent ?? false}
          isRoomForRentDisabled={isRoomForRentDisabled}
          toggleRoomForRentChecked={handleToggleRoomForRent}
        />
      )}
    </div>
  );
};

export default AddPropertyForm;
